import { EOrganization, ESnapshot, exists } from 'lib/types';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { getInheritedProperty } from 'lib/utils/inheritance';
import { isPublisherOrganization } from 'lib/utils/organizations';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { useBooleanFlag } from 'utils/flags';

/**
 * Determines whether the Publisher Invoices tab on the payments page is
 * available for the given organization: the publisher invoice table flag is
 * on and the organization is (or inherits from) a publisher configured for
 * Column invoicing via Stripe subscriptions.
 */
export default function usePublisherInvoicesTabEnabled(
  organization: ESnapshot<EOrganization> | null
): { enabled: boolean; isLoading: boolean } {
  const enablePublisherInvoiceTable = useBooleanFlag(
    LaunchDarklyFlags.ENABLE_PUBLISHER_INVOICE_TABLE
  );

  const { value: payColumnInvoices, isLoading: loadingPayColumnInvoices } =
    useAsyncEffect({
      fetchData: async () => {
        if (
          !enablePublisherInvoiceTable ||
          !exists(organization) ||
          !isPublisherOrganization(organization)
        ) {
          return null;
        }
        return await getInheritedProperty(
          organization.ref,
          'payColumnInvoices'
        );
      },
      dependencies: [organization?.id, enablePublisherInvoiceTable]
    });

  const enabled =
    enablePublisherInvoiceTable &&
    payColumnInvoices?.enabled === true &&
    !!payColumnInvoices.stripeSubscriptionId;

  return {
    enabled,
    isLoading: enablePublisherInvoiceTable && loadingPayColumnInvoices
  };
}
